"use client";

import { useQuery } from "@tanstack/react-query";
import {
  TalentNetworkSummarySchema,
  TalentNetworkSessionSchema,
  TalentNetworkJoinResponseSchema,
  type TalentNetworkSessionDto,
  type TalentNetworkSummaryDto,
} from "@artha/api-contracts";
import { API_ROUTES } from "@artha/types";
import { unwrapApiData as unwrap } from "@/lib/apiResponse";
import type { ApiEnvelope } from "@/lib/apiResponse.types";
import type { SaveTalentNetworkProfileInput } from "../talentNetwork.types";
import { apiFetch } from "@/lib/apiClient";
import { getApiError } from "@/modules/candidate-personalization/services/personalizationClient";
import { TALENT_NETWORK_JOIN_ERROR, TALENT_NETWORK_SUMMARY_QUERY_KEY } from "../talentNetwork.constants";


export const fetchTalentNetworkSummary = async (username: string) => {
  const response = await apiFetch<ApiEnvelope<TalentNetworkSummaryDto>>(
    API_ROUTES.TALENT_NETWORK.SUMMARY(username),
  );
  if (!response.ok) throw new Error("Failed to load Talent Network summary");
  return TalentNetworkSummarySchema.parse(unwrap(response.data));
};

export const useTalentNetworkSummary = (username: string) =>
  useQuery<TalentNetworkSummaryDto>({
    queryKey: TALENT_NETWORK_SUMMARY_QUERY_KEY(username),
    queryFn: () => fetchTalentNetworkSummary(username),
    enabled: Boolean(username),
    staleTime: 60_000,
  });

export const fetchTalentNetworkSession = async (username: string) => {
  const response = await apiFetch<
    TalentNetworkSessionDto | { data: TalentNetworkSessionDto }
  >(API_ROUTES.TALENT_NETWORK.SESSION(username));
  const result = unwrap(response.data);
  if (!response.ok || !result)
    throw new Error("Unable to load Talent Network profile");
  return TalentNetworkSessionSchema.parse(result);
};

export const joinTalentNetwork = async (username: string) => {
  const response = await apiFetch<
    TalentNetworkSessionDto | { data: TalentNetworkSessionDto }
  >(API_ROUTES.TALENT_NETWORK.JOIN(username), {
    method: "POST",
    body: JSON.stringify({ consent: true }),
  });
  if (!response.ok) throw getApiError(response.data, TALENT_NETWORK_JOIN_ERROR);
  return TalentNetworkJoinResponseSchema.parse(unwrap(response.data));
};

export const saveTalentNetworkProfile = async (input: SaveTalentNetworkProfileInput) => {
  const response = await apiFetch<
    TalentNetworkSessionDto | { data: TalentNetworkSessionDto }
  >(API_ROUTES.TALENT_NETWORK.PROFILE(input.username), {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  const result = unwrap(response.data);
  if (!response.ok || !result)
    throw new Error("Unable to save Talent Network profile");
  return TalentNetworkSessionSchema.parse(result);
};
